import React, { useState, useEffect, useCallback } from 'react';
import { getGalleryPhotos } from '../api';
import { IconClose } from './Icons';

// Personal garden photos, converted server-side from the original
// HEIC/JPEG originals with all EXIF/GPS metadata stripped. Served as
// static assets from the public/ folder (CRA copies public/ into the
// build output verbatim). This is permanent seed content - new admin
// uploads (see AdminGalleryUpload) are merged in live from the API
// below, so they appear without needing a rebuild/redeploy.
const FLOWER_COUNT = 50;
const VEGETABLE_COUNT = 34;

const seedFlowerPhotos = Array.from({ length: FLOWER_COUNT }, (_, i) =>
  `/gallery/flowers/flower-${String(i + 1).padStart(3, '0')}.jpg`
);
const seedVegetablePhotos = Array.from({ length: VEGETABLE_COUNT }, (_, i) =>
  `/gallery/vegetables/vegetable-${String(i + 1).padStart(3, '0')}.jpg`
);

const TABS = [
  { key: 'flowers', label: 'Flowers', seed: seedFlowerPhotos },
  { key: 'vegetables', label: 'Vegetables', seed: seedVegetablePhotos },
  { key: 'lawn', label: 'Lawn', seed: [] },
  { key: 'other', label: 'Other', seed: [] },
];

// Show a manageable number at a time with a "show more" button rather
// than rendering all images at once.
const PAGE_SIZE = 12;

export default function PhotoGallery() {
  const [opened, setOpened] = useState(false);
  const [tab, setTab] = useState('flowers');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [lightbox, setLightbox] = useState(null);
  const [livePhotos, setLivePhotos] = useState({});
  const [loadingLive, setLoadingLive] = useState(false);

  // Fetch live (admin-uploaded) photos for a category once, the first
  // time its tab is opened, so we're not hitting the API for tabs the
  // visitor never looks at.
  useEffect(() => {
    if (!opened || livePhotos[tab] !== undefined) return;
    setLoadingLive(true);
    getGalleryPhotos(tab)
      .then(data => {
        const urls = (data.photos || []).map(p => p.url).filter(Boolean);
        setLivePhotos(prev => ({ ...prev, [tab]: urls }));
      })
      .catch(() => setLivePhotos(prev => ({ ...prev, [tab]: [] })))
      .finally(() => setLoadingLive(false));
  }, [opened, tab, livePhotos]);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  // Close the lightbox on Escape as well as on click.
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, closeLightbox]);

  const active = TABS.find(t => t.key === tab);
  // Live (newest) uploads first, then the permanent seed collection.
  const allPhotos = [...(livePhotos[tab] || []), ...active.seed];
  const shown = allPhotos.slice(0, visible);

  const switchTab = (key) => {
    setTab(key);
    setVisible(PAGE_SIZE);
  };

  return (
    <section className="section" aria-labelledby="gallery-heading">
      <div className="section-head">
        <div>
          <p className="eyebrow">Photographs</p>
          <h2 className="section-title" id="gallery-heading">From the garden</h2>
        </div>
        {!opened && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpened(true)}>
            View gallery
          </button>
        )}
      </div>

      {opened && (
        <>
          <div className="gallery__tabs chips" role="group" aria-label="Photo categories">
            {TABS.map(t => (
              <button
                type="button"
                key={t.key}
                className={tab === t.key ? 'chip is-active' : 'chip'}
                aria-pressed={tab === t.key}
                onClick={() => switchTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loadingLive && allPhotos.length === 0 ? (
            <p className="empty-state">Loading photographs...</p>
          ) : allPhotos.length === 0 ? (
            <p className="empty-state">No photographs in this category yet.</p>
          ) : (
            <div className="photos-grid">
              {shown.map(src => (
                <button
                  type="button"
                  className="photo-item"
                  key={src}
                  onClick={() => setLightbox(src)}
                  aria-label="Open photograph"
                >
                  <img src={src} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}

          {visible < allPhotos.length && (
            <button
              type="button"
              className="btn btn-secondary btn-sm gallery__more"
              onClick={() => setVisible(v => v + PAGE_SIZE)}
            >
              Show more
            </button>
          )}
        </>
      )}

      {lightbox && (
        <div className="lightbox" onClick={closeLightbox} role="dialog" aria-modal="true" aria-label="Photograph">
          <button type="button" className="lightbox__close" onClick={closeLightbox} aria-label="Close">
            <IconClose size={22} />
          </button>
          <img src={lightbox} alt="Garden photograph, enlarged" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </section>
  );
}
